import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

// ── Remote Config flag data (derived from feature_flag_service.dart) ──

const CAPABILITY_FLAGS = {
  'Free': [
    { key: 'enable_club_picker', default: true },
    { key: 'enable_public_docs', default: true },
    { key: 'enable_club_branding', default: true },
  ],
  'Sailor': [
    { key: 'enable_course_card_popup', default: true },
    { key: 'enable_mark_identification', default: true },
    { key: 'enable_rounding_direction', default: true },
    { key: 'enable_nav_header', default: true },
    { key: 'enable_prev_next_buttons', default: true },
  ],
  'Navigator': [
    { key: 'enable_btw_dtw', default: true },
    { key: 'enable_next_mark_preview', default: true },
    { key: 'enable_auto_advance', default: true },
    { key: 'enable_race_timer', default: true },
    { key: 'enable_live_activity', default: true },
    { key: 'enable_weather_overlay', default: true },
    { key: 'enable_custom_course', default: true },
    { key: 'enable_next_leg_btw_dtw', default: true },
    { key: 'enable_next_leg_point_of_sail', default: true },
    { key: 'enable_point_of_sail', default: true },
    { key: 'enable_laylines', default: true },
    { key: 'enable_time_to_start', default: true },
    { key: 'enable_audio_prompts', default: true },
    { key: 'enable_watch_app', default: true },
    { key: 'enable_nmea_integration', default: true },
    { key: 'enable_gpx_export', default: true },
  ],
};

const NON_CAP_FLAGS = [
  { key: 'tier_override', default: '""', type: 'string', desc: 'Override pricing tier for all users' },
  { key: 'enable_invite_codes', default: 'false', type: 'bool', desc: 'Enable beta invite code redemption' },
  { key: 'send_debug_to_logrocket', default: 'false', type: 'bool', desc: 'Forward debugPrint to LogRocket' },
  { key: 'support_email', default: 'support@racemarks.app', type: 'string', desc: 'Configurable support email' },
  { key: 'support_phone', default: '+35315365358', type: 'string', desc: 'Configurable support phone' },
];

// App reads any RC key matching `promo_*` or `promoN` at runtime. These
// entries are the currently-known slots; new campaigns can be added in the
// Remote Config console using either a generic slot or a descriptive name
// without an app deploy.
const PROMO_FLAGS = [
  { key: 'promo1', desc: 'Generic promo slot #1 (empty by default)', events: '—', window: '—' },
  { key: 'promo2', desc: 'Generic promo slot #2 (empty by default)', events: '—', window: '—' },
  { key: 'promo3', desc: 'Generic promo slot #3 (empty by default)', events: '—', window: '—' },
  { key: 'promo4', desc: 'Generic promo slot #4 (empty by default)', events: '—', window: '—' },
  { key: 'promo_hyc_spring2026', desc: 'Free sailor access to DBSC Summer 2026 (legacy key)', events: 'ie-dublin-bay/2026-summer', window: 'Apr–Oct 2026' },
];

// ── Sub-components ──

function AxisCard({ title, values, control, defaultVal, color }) {
  return (
    <div className="cl-axis-card" style={{ borderTopColor: color }}>
      <h4>{title}</h4>
      <div className="cl-axis-values">
        {values.map((v) => (
          <span key={v} className={`cl-axis-val${v === defaultVal ? ' cl-axis-default' : ''}`}>
            {v}{v === defaultVal && <span className="cl-default-tag">default</span>}
          </span>
        ))}
      </div>
      <div className="cl-axis-control">{control}</div>
    </div>
  );
}

function ProjectCard({ name, projectId, color, targets }) {
  return (
    <div className="cl-project-card" style={{ borderLeftColor: color }}>
      <h4>{name}</h4>
      <code className="cl-project-id">{projectId}</code>
      <div className="cl-hosting-targets">
        {targets.map(([label, site]) => (
          <div key={label} className="cl-hosting-row">
            <span className="cl-hosting-label">{label}</span>
            <code>{site}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

function FlagRow({ flag, isOn }) {
  return (
    <div className="cl-flag-row">
      <code>{flag}</code>
      <span className={`cl-flag-default ${isOn ? 'cl-flag-on' : 'cl-flag-off'}`}>
        {isOn ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

// ── Main page ──

export default function ConfigLevers() {
  return (
    <>
      <PortalHeader />
      <div className="page" style={{ maxWidth: 1100 }}>
        <PageHeader backTo="/admin" backLabel="Admin" title="Configuration Levers" />

        {/* ─── Section 1: Build Matrix ─────────────────────────── */}
        <section className="cl-section">
          <h2>Build Matrix</h2>
          <p className="cl-subtitle">
            Three independent axes control every build. Any combination is valid.
          </p>

          <div className="cl-axis-grid">
            <AxisCard
              title="Build Tier"
              values={['internal', 'beta', 'public']}
              defaultVal="public"
              control={<>Env var <code>TIER</code></>}
              color="#e65100"
            />
            <AxisCard
              title="Build Mode"
              values={['debug', 'release']}
              defaultVal="debug"
              control={<>Flutter <code>kDebugMode</code></>}
              color="#1565c0"
            />
            <AxisCard
              title="Environment"
              values={['staging', 'prod']}
              defaultVal="staging"
              control={<><code>--dart-define=ENV=</code></>}
              color="#2e7d32"
            />
          </div>

          <h3 className="cl-sub-heading">Common Combinations</h3>
          <table className="cl-combo-table">
            <thead>
              <tr><th>Scenario</th><th>Tier</th><th>Mode</th><th>Env</th><th>Command</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Local dev</td>
                <td><code>internal</code></td>
                <td><code>debug</code></td>
                <td><code>staging</code></td>
                <td><code>TIER=internal flutter run</code></td>
              </tr>
              <tr>
                <td>Emulator dev</td>
                <td><code>internal</code></td>
                <td><code>debug</code></td>
                <td><code>staging</code></td>
                <td><code>TIER=internal flutter run --dart-define=USE_EMULATOR=true</code></td>
              </tr>
              <tr>
                <td>Staging debug</td>
                <td><code>internal</code></td>
                <td><code>debug</code></td>
                <td><code>staging</code></td>
                <td><code>tools/make_staging.sh</code></td>
              </tr>
              <tr>
                <td>Staging release</td>
                <td><code>internal</code></td>
                <td><code>release</code></td>
                <td><code>staging</code></td>
                <td><code>tools/make_staging.sh --release</code></td>
              </tr>
              <tr>
                <td>TestFlight</td>
                <td><code>beta</code></td>
                <td><code>release</code></td>
                <td><code>prod</code></td>
                <td><code>TIER=beta tools/build_ios.sh</code></td>
              </tr>
              <tr>
                <td>App Store</td>
                <td><code>public</code></td>
                <td><code>release</code></td>
                <td><code>prod</code></td>
                <td><code>TIER=public tools/build_ios.sh</code></td>
              </tr>
              <tr>
                <td>Play Store</td>
                <td><code>public</code></td>
                <td><code>release</code></td>
                <td><code>prod</code></td>
                <td><code>TIER=public tools/build_android.sh</code></td>
              </tr>
            </tbody>
          </table>

          <div className="cl-note">
            <strong>Build Tier</strong> controls L1 compile-time capability flags.
            <strong> Build Mode</strong> controls debug overrides, emulator access, and log verbosity.
            <strong> Environment</strong> selects the Firebase project and API keys.<br /><br />
            <strong>Staging debug vs release:</strong> Both use staging Firebase and internal tier.
            The key difference is <strong>RevenueCat keys</strong> &mdash;
            staging debug uses <em>test</em> keys (no purchase flow needed for hot reload),
            while staging release uses <em>production</em> keys (real sandbox purchases work;
            test keys cause a &ldquo;Wrong API Key&rdquo; error in release mode).
            The <code>make_staging.sh</code> script handles this automatically.
          </div>
        </section>

        {/* ─── Section 2: Firebase Projects ────────────────────── */}
        <section className="cl-section">
          <h2>Firebase Projects</h2>
          <p className="cl-subtitle">
            Two Firebase projects for isolation. Selected via <code>--dart-define=ENV=staging|prod</code>.
          </p>

          <div className="cl-project-grid">
            <ProjectCard
              name="Staging"
              projectId="racemarksapp-dev"
              color="#ff9800"
              targets={[
                ['Website', 'racemarksapp-dev'],
                ['Committee', 'racemarksapp-dev-committee'],
                ['App Preview', 'staging.app-preview-racemarks.pages.dev'],
              ]}
            />
            <ProjectCard
              name="Production"
              projectId="racemarksapp"
              color="#4caf50"
              targets={[
                ['Website', 'racemarksapp'],
                ['Committee', 'racemarksapp-committee'],
              ]}
            />
          </div>

          <h3 className="cl-sub-heading">Platform Selection</h3>
          <table className="cl-platform-table">
            <thead>
              <tr><th>Platform</th><th>Mechanism</th><th>Files</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Flutter (all)</td>
                <td><code>--dart-define=ENV=staging|prod</code></td>
                <td><code>lib/core/config/firebase_init.dart</code></td>
              </tr>
              <tr>
                <td>iOS</td>
                <td>GoogleService-Info plist swap</td>
                <td>
                  <code>ios/Runner/GoogleService-Info-Staging.plist</code><br />
                  <code>ios/Runner/GoogleService-Info-Prod.plist</code><br />
                  <code>tools/select-firebase-env.sh</code>
                </td>
              </tr>
              <tr>
                <td>Android</td>
                <td>Gradle flavor directories</td>
                <td>
                  <code>android/app/src/staging/google-services.json</code><br />
                  <code>android/app/src/prod/google-services.json</code>
                </td>
              </tr>
              <tr>
                <td>Web (committee)</td>
                <td><code>.env</code> files</td>
                <td>
                  <code>web/committee/.env.staging</code><br />
                  <code>web/committee/.env.example</code>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="cl-note">
            <strong>Emulators:</strong> <code>--dart-define=USE_EMULATOR=true</code> (debug only)
            connects Firestore to <code>127.0.0.1:8080</code> and Storage to <code>127.0.0.1:9199</code>.
          </div>
        </section>

        {/* ─── Section 3: Firebase Remote Config ───────────────── */}
        <section className="cl-section">
          <h2>Firebase Remote Config</h2>
          <p className="cl-subtitle">
            Layer 2 kill switches. All capability flags default to <strong>ON</strong> (fail-open).
            Managed in the <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer">Firebase Console</a>.
          </p>

          <div className="cl-rc-grid">
            {Object.entries(CAPABILITY_FLAGS).map(([tier, flags]) => (
              <div key={tier} className="cl-rc-tier">
                <h4>{tier} Tier <span className="cl-rc-count">{flags.length}</span></h4>
                {flags.map((f) => (
                  <FlagRow key={f.key} flag={f.key} isOn={f.default} />
                ))}
              </div>
            ))}
          </div>

          <h3 className="cl-sub-heading">Non-Capability Flags</h3>
          <table className="cl-noncap-table">
            <thead>
              <tr><th>Key</th><th>Default</th><th>Type</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              {NON_CAP_FLAGS.map((f) => (
                <tr key={f.key}>
                  <td><code>{f.key}</code></td>
                  <td><code>{f.default}</code></td>
                  <td>{f.type}</td>
                  <td>{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="cl-sub-heading">Promo Flags (JSON)</h3>
          <div className="cl-promo-grid">
            {PROMO_FLAGS.map((p) => (
              <div key={p.key} className="cl-promo-card">
                <code>{p.key}</code>
                <div className="cl-promo-desc">{p.desc}</div>
                <div className="cl-promo-meta">
                  <span>Events: <code>{p.events}</code></span>
                  <span>Window: {p.window}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Section 4: Secrets & API Keys ───────────────────── */}
        <section className="cl-section">
          <h2>Secrets &amp; API Keys</h2>
          <p className="cl-subtitle">
            Generated by <code>dart run tools/gen/generate_secrets.dart --rig=development|production</code>.
            Output: <code>lib/core/config/secrets.g.dart</code> (gitignored).
          </p>

          <div className="cl-secrets-grid">
            <div className="cl-secret-card">
              <h4>RevenueCat</h4>
              <div className="cl-secret-keys">
                <div className="cl-secret-rig">
                  <span className="cl-rig-label cl-rig-dev">Development</span>
                  <code>REVENUECAT_APPSTORE_API_KEY_DEVELOPMENT</code>
                  <code>REVENUECAT_PLAYSTORE_API_KEY_DEVELOPMENT</code>
                </div>
                <div className="cl-secret-rig">
                  <span className="cl-rig-label cl-rig-prod">Production</span>
                  <code>REVENUECAT_APPSTORE_API_KEY_PRODUCTION</code>
                  <code>REVENUECAT_PLAYSTORE_API_KEY_PRODUCTION</code>
                </div>
              </div>
              <div className="cl-secret-source">
                Source: <code>config/secrets.yaml</code> or env vars
              </div>
            </div>

            <div className="cl-secret-card">
              <h4>LogRocket</h4>
              <div className="cl-secret-keys">
                <div className="cl-secret-rig">
                  <span className="cl-rig-label cl-rig-dev">Development</span>
                  <code>LOGROCKET_APP_ID_DEVELOPMENT</code>
                </div>
                <div className="cl-secret-rig">
                  <span className="cl-rig-label cl-rig-prod">Production</span>
                  <code>LOGROCKET_APP_ID_PRODUCTION</code>
                </div>
              </div>
              <div className="cl-secret-source">
                Source: <code>config/secrets.yaml</code> or env vars
              </div>
            </div>

            <div className="cl-secret-card">
              <h4>Android Signing</h4>
              <div className="cl-secret-keys">
                <code>android/app/key.properties</code>
                <span className="cl-secret-note">Keystore path, passwords, key alias (gitignored)</span>
              </div>
            </div>
          </div>

          <div className="cl-note">
            <strong>Provisioning:</strong> Use 1Password CLI (<code>op run --env-file=.env -- dart run ...</code>),
            Google Secret Manager, or a local <code>config/secrets.yaml</code> (gitignored).
          </div>
        </section>

        {/* ─── Section 5: Platform Build Configs ───────────────── */}
        <section className="cl-section">
          <h2>Platform Build Configs</h2>
          <p className="cl-subtitle">
            Platform-specific configuration for iOS and Android builds.
          </p>

          <div className="cl-platform-grid">
            <div className="cl-platform-card">
              <h4>iOS</h4>
              <table className="cl-files-table">
                <tbody>
                  <tr><td><code>ios/Capabilities.generated.xcconfig</code></td><td>Generated from capabilities.yaml</td></tr>
                  <tr><td><code>ios/Flutter/Debug.xcconfig</code></td><td>Flutter debug config</td></tr>
                  <tr><td><code>ios/Flutter/Release.xcconfig</code></td><td>Flutter release config</td></tr>
                  <tr><td><code>ios/Products.storekit</code></td><td>StoreKit testing (locale: en_GB, storefront: IRL)</td></tr>
                  <tr><td><code>tools/select-firebase-env.sh</code></td><td>Switches GoogleService-Info plist</td></tr>
                  <tr><td><code>tools/build_ios.sh</code></td><td>Build script (reads TIER env var)</td></tr>
                </tbody>
              </table>
              <div className="cl-note" style={{ marginTop: '0.75rem' }}>
                Xcode schemes: <code>Runner</code> (prod), <code>staging</code> (staging).
                Build configs: <code>Debug</code> / <code>Release</code> / <code>Debug staging</code> / <code>Release staging</code>.<br />
                App IDs: <code>ie.digimill.marine.racemarksapp</code> (prod),
                <code> ie.digimill.marine.racemarksapp.staging</code> (staging).
                Use <code>--flavor staging</code> to install as a separate app.
              </div>
            </div>

            <div className="cl-platform-card">
              <h4>Android</h4>
              <table className="cl-files-table">
                <tbody>
                  <tr><td><code>android/app/build.gradle.kts</code></td><td>Flavors: staging, prod</td></tr>
                  <tr>
                    <td><code>android/app/src/staging/</code></td>
                    <td>Staging google-services.json (<code>racemarksapp-dev</code>)</td>
                  </tr>
                  <tr>
                    <td><code>android/app/src/prod/</code></td>
                    <td>Prod google-services.json (<code>racemarksapp</code>)</td>
                  </tr>
                  <tr>
                    <td><code>android/app/key.properties</code></td>
                    <td>Signing config (gitignored)</td>
                  </tr>
                  <tr><td><code>tools/build_android.sh</code></td><td>Build script (reads TIER env var)</td></tr>
                </tbody>
              </table>
              <div className="cl-note" style={{ marginTop: '0.75rem' }}>
                App IDs: <code>ie.digimill.marine.racemarksapp</code> (prod),
                <code> ie.digimill.marine.racemarksapp.staging</code> (staging)
              </div>
            </div>
          </div>

          <h3 className="cl-sub-heading">Config Pipeline</h3>
          <pre className="cl-pipeline">{`config/capabilities.yaml            ← EDIT THIS
    │
    ├──→ lib/core/features/capabilities.gen.dart     (Dart const bools)
    ├──→ ios/Capabilities.generated.xcconfig          (Xcode build settings)
    ├──→ ios/Runner/Capabilities.generated.swift      (Swift constants)
    └──→ firebase/remote_config_template.json         (Firebase Remote Config)

config/entitlements.yaml             ← products & pricing
    │
    ├──→ ios/Products.storekit                        (StoreKit testing)
    └──→ RevenueCat dashboard                         (manual sync!)

config/secrets.yaml                  ← API keys (gitignored)
    │
    └──→ lib/core/config/secrets.g.dart               (generated, gitignored)`}</pre>
        </section>

        {/* ─── Section 6: Observability & Consent ──────────────── */}
        <section className="cl-section">
          <h2>Observability &amp; Consent</h2>
          <p className="cl-subtitle">
            Session recording and crash reporting, controlled by build tier and user consent.
          </p>

          <div className="cl-consent-grid">
            <div className="cl-consent-card">
              <h4>LogRocket Session Recording</h4>
              <div className="cl-consent-flow">
                <div className="cl-consent-row">
                  <span className="cl-consent-tier cl-tier-int">Internal / Beta</span>
                  <span className="cl-consent-arrow">&rarr;</span>
                  <span className="cl-consent-result cl-consent-on">Always ON</span>
                </div>
                <div className="cl-consent-row">
                  <span className="cl-consent-tier cl-tier-pub">Public</span>
                  <span className="cl-consent-arrow">&rarr;</span>
                  <span className="cl-consent-result cl-consent-gate">User opt-in</span>
                </div>
              </div>
              <div className="cl-consent-detail">
                <code>pref_share_usage_data</code> &mdash; SharedPreferences toggle in Settings.
                GDPR requires opt-in for session recording in public builds.
              </div>
            </div>

            <div className="cl-consent-card">
              <h4>Crash Reports</h4>
              <div className="cl-consent-flow">
                <div className="cl-consent-row">
                  <span className="cl-consent-tier cl-tier-int">Internal / Beta</span>
                  <span className="cl-consent-arrow">&rarr;</span>
                  <span className="cl-consent-result cl-consent-on">Always ON</span>
                </div>
                <div className="cl-consent-row">
                  <span className="cl-consent-tier cl-tier-pub">Public</span>
                  <span className="cl-consent-arrow">&rarr;</span>
                  <span className="cl-consent-result cl-consent-on">ON (opt-out)</span>
                </div>
              </div>
              <div className="cl-consent-detail">
                <code>pref_crash_reports</code> &mdash; SharedPreferences toggle in Settings.
                Defaults to <strong>true</strong> &mdash; user can disable in Settings.
              </div>
            </div>

            <div className="cl-consent-card">
              <h4>Debug Forwarding</h4>
              <div className="cl-consent-detail">
                Remote Config flag <code>send_debug_to_logrocket</code> (default: OFF).
                When ON, forwards <code>debugPrint</code> output to LogRocket for remote debugging.
              </div>
            </div>
          </div>

          <h3 className="cl-sub-heading">Key Files</h3>
          <table className="cl-files-table" style={{ maxWidth: 700 }}>
            <tbody>
              <tr><td><code>lib/core/config/logrocket_consent.dart</code></td><td>Consent logic + tier check</td></tr>
              <tr><td><code>lib/core/config/logrocket_config.dart</code></td><td>LogRocket initialization</td></tr>
              <tr><td><code>lib/core/config/secrets.g.dart</code></td><td>LogRocket app ID (generated)</td></tr>
            </tbody>
          </table>
        </section>

      </div>
    </>
  );
}
